import React from 'react';
import styled from 'styled-components';
import { easePop } from './motion';

const ACCENT = '#FF6B00';
const INK = '#1c1c1c';
const LINE = '#f0ece8';
const MUTED = '#9b9794';

const Wrap = styled.div`
  will-change: transform, opacity;
`;

const Window = styled.div`
  width: 336px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #ebe6e1;
  box-shadow: 0 28px 64px -30px rgba(28, 28, 28, 0.32);
  overflow: hidden;
  transition:
    transform 320ms cubic-bezier(0.22, 0.9, 0.24, 1),
    box-shadow 320ms ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 36px 76px -30px rgba(28, 28, 28, 0.4);
  }
`;

/* Browser chrome, in colour — the resolved counterpart to the grey application
   windows piled up on the other side of the headline. */
const BrowserBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 12px;
  background: #faf8f6;
  border-bottom: 1px solid ${LINE};
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e2ded9;
`;

const UrlPill = styled.span`
  flex: 1;
  margin-left: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${LINE};
  font-size: 8.5px;
  color: ${MUTED};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AppBody = styled.div`
  display: flex;
  align-items: stretch;
`;

const IconRail = styled.div`
  width: 44px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 0;
  background: #fbfaf9;
  border-right: 1px solid ${LINE};
`;

const LogoTile = styled.span`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  margin-bottom: 8px;
  border-radius: 7px;
  background: ${ACCENT};
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
`;

const RailIcon = styled.span<{ $active?: boolean }>`
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: ${(props) => (props.$active ? 'rgba(255, 107, 0, 0.12)' : 'transparent')};
  color: ${(props) => (props.$active ? ACCENT : '#c2bdb9')};
`;

const RAIL_ICONS: React.ReactNode[] = [
  <>
    <rect x="2.5" y="2.5" width="5" height="5" rx="1" />
    <rect x="10.5" y="2.5" width="5" height="5" rx="1" />
    <rect x="2.5" y="10.5" width="5" height="5" rx="1" />
    <rect x="10.5" y="10.5" width="5" height="5" rx="1" />
  </>,
  <>
    <path d="M2.5 9.5h4l1.5 2.5h2l1.5-2.5h4" />
    <path d="M4 3.5h10l1.5 6v5h-13v-5z" />
  </>,
  <>
    <path d="M9 2.5l6 3v7l-6 3-6-3v-7z" />
    <path d="M3 5.5l6 3 6-3" />
    <path d="M9 8.5v7" />
  </>,
  <>
    <path d="M2.5 9.5h3l2-5 3 10 2-5h3" />
  </>,
  <>
    <path d="M3 14.5v-5" />
    <path d="M7.5 14.5v-9" />
    <path d="M12 14.5v-6" />
    <path d="M15.5 3.5v11" />
  </>,
];

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  border-bottom: 1px solid ${LINE};
`;

const PageTitle = styled.span`
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${INK};
`;

const LivePill = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.1);
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #1e8e4e;
`;

const LiveDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #22c55e;
`;

const Avatar = styled.span`
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  margin-left: auto;
  border-radius: 50%;
  background: #f0ece8;
  font-size: 8px;
  font-weight: 700;
  color: #6b6865;
`;

const Pad = styled.div`
  padding: 12px 14px 14px;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const Kpi = styled.div`
  padding: 9px 10px;
  border-radius: 9px;
  border: 1px solid ${LINE};
  background: #ffffff;
`;

const KpiLabel = styled.div`
  font-size: 7.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: ${MUTED};
`;

const KpiValue = styled.div`
  margin-top: 3px;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${INK};
`;

const KpiDelta = styled.span<{ $down?: boolean }>`
  margin-left: 5px;
  font-size: 9px;
  font-weight: 700;
  color: ${(props) => (props.$down ? '#1e8e4e' : ACCENT)};
`;

const Panel = styled.div`
  margin-top: 10px;
  padding: 10px 10px 6px;
  border-radius: 9px;
  border: 1px solid ${LINE};
`;

const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 6px;
`;

const PanelTitle = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  color: #44413e;
`;

const PanelMeta = styled.span`
  margin-left: auto;
  font-size: 8px;
  color: ${MUTED};
`;

const BARS = [26, 34, 30, 42, 38, 52, 47, 64];
const BAR_W = 18;
const BAR_GAP = 9;
const CHART_H = 68;

const Chart: React.FC = () => (
  <svg
    viewBox={`0 0 ${BARS.length * (BAR_W + BAR_GAP) - BAR_GAP} ${CHART_H}`}
    width="100%"
    height={CHART_H}
    preserveAspectRatio="none"
    aria-hidden="true"
    focusable="false"
  >
    {BARS.map((value, i) => (
      <rect
        key={i}
        x={i * (BAR_W + BAR_GAP)}
        y={CHART_H - value}
        width={BAR_W}
        height={value}
        rx={3}
        fill={i === BARS.length - 1 ? ACCENT : '#f2ede9'}
      />
    ))}
  </svg>
);

const Table = styled.div`
  margin-top: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  border-top: 1px solid ${LINE};

  &:last-child {
    border-bottom: 1px solid ${LINE};
  }
`;

const RowName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 10.5px;
  color: #3c3a38;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RowStage = styled.span`
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: ${MUTED};
`;

const StatusPill = styled.span<{ $tone: 'active' | 'queued' | 'done' }>`
  width: 46px;
  text-align: center;
  padding: 2px 0;
  border-radius: 999px;
  font-size: 8px;
  font-weight: 700;
  background: ${(props) =>
    props.$tone === 'active'
      ? 'rgba(255, 107, 0, 0.12)'
      : props.$tone === 'done'
        ? 'rgba(34, 197, 94, 0.12)'
        : '#f4f1ee'};
  color: ${(props) =>
    props.$tone === 'active' ? ACCENT : props.$tone === 'done' ? '#1e8e4e' : '#a3a09d'};
`;

const ROWS: Array<[string, string, 'active' | 'queued' | 'done']> = [
  ['Acme Co.', 'INTAKE', 'active'],
  ['Northwind', 'BUILD', 'queued'],
  ['Contoso', 'SHIP', 'done'],
];

interface DashboardAppProps {
  /** 0..1 entrance progress. */
  progress: number;
}

export const DashboardApp: React.FC<DashboardAppProps> = ({ progress }) => {
  const entered = easePop(progress);
  const settle = 1 - entered;

  return (
    <Wrap
      aria-hidden="true"
      style={{
        opacity: entered,
        transform:
          `translateY(${20 * settle}px) rotate(${1.6 * settle}deg) ` +
          `scale(${0.96 + 0.04 * entered})`,
      }}
    >
      <Window>
        <BrowserBar>
          <Dot />
          <Dot />
          <Dot />
          <UrlPill>app.uptrendsystems.com/operations</UrlPill>
        </BrowserBar>
        <AppBody>
          <IconRail>
            <LogoTile>U</LogoTile>
            {RAIL_ICONS.map((glyph, i) => (
              <RailIcon key={i} $active={i === 0}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 18 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {glyph}
                </svg>
              </RailIcon>
            ))}
          </IconRail>
          <Content>
            <TopBar>
              <PageTitle>Operations</PageTitle>
              <LivePill>
                <LiveDot />
                LIVE
              </LivePill>
              <Avatar>AR</Avatar>
            </TopBar>
            <Pad>
              <KpiRow>
                <Kpi>
                  <KpiLabel>JOBS TODAY</KpiLabel>
                  <KpiValue>
                    284
                    <KpiDelta>+12%</KpiDelta>
                  </KpiValue>
                </Kpi>
                <Kpi>
                  <KpiLabel>AVG CYCLE</KpiLabel>
                  <KpiValue>
                    3.2h
                    <KpiDelta $down>−41%</KpiDelta>
                  </KpiValue>
                </Kpi>
              </KpiRow>
              <Panel>
                <PanelHead>
                  <PanelTitle>Throughput</PanelTitle>
                  <PanelMeta>last 8 weeks</PanelMeta>
                </PanelHead>
                <Chart />
              </Panel>
              <Table>
                {ROWS.map(([name, stage, tone]) => (
                  <Row key={name}>
                    <RowName>{name}</RowName>
                    <RowStage>{stage}</RowStage>
                    <StatusPill $tone={tone}>
                      {tone === 'active' ? 'Active' : tone === 'done' ? 'Done' : 'Queued'}
                    </StatusPill>
                  </Row>
                ))}
              </Table>
            </Pad>
          </Content>
        </AppBody>
      </Window>
    </Wrap>
  );
};
